import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { FiAlertTriangle, FiX } from 'react-icons/fi';
import useAxiosWithAuth from '../hooks/useAxiosWithAuth';

// أنماط التنسيق
const WarningContainer = styled(motion.div)`
  position: fixed;
  bottom: 20px;
  left: 20px;
  background-color: white;
  border-radius: 8px;
  border-right: 4px solid var(--error);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  padding: 16px;
  width: 320px;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const WarningHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const WarningIcon = styled.div`
  color: var(--error);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const WarningTitle = styled.h4`
  margin: 0;
  color: var(--gray-800);
  font-size: 1rem;
  font-weight: 600;
  flex: 1;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: var(--gray-500);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s;
  
  &:hover {
    background-color: var(--gray-100);
    color: var(--gray-700);
  }
`;

const WarningMessage = styled.p`
  margin: 0;
  color: var(--gray-700);
  font-size: 0.9rem;
  line-height: 1.5;
`;

const ButtonsContainer = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 4px;
`;

const ActionButton = styled.button`
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  flex: ${props => props.primary ? '1' : 'none'};
  
  &.primary {
    background-color: var(--error);
    color: white;
    border: none;
    
    &:hover {
      background-color: var(--error-dark, #b91c1c);
    }
  }
  
  &.secondary {
    background-color: transparent;
    color: var(--gray-600);
    border: 1px solid var(--gray-300);
    
    &:hover {
      background-color: var(--gray-100);
    }
  }
`;

// مكون تحذير التحقق من البريد الإلكتروني
const EmailVerificationWarning = () => {
  const [isVisible, setIsVisible] = useState(false);
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const axiosWithAuth = useAxiosWithAuth();
  
  // التحقق من حالة التحقق من البريد الإلكتروني عند تحميل المكون
  useEffect(() => {
    const checkVerificationStatus = async () => {
      try {
        // تحديث بيانات المستخدم للحصول على أحدث حالة للتحقق
        const response = await axiosWithAuth.get('/api/users/me');
        
        // عرض التحذير فقط إذا كان البريد غير مفعل وليس في صفحة التحقق
        if (response.data && response.data.is_verified === false && location.pathname !== '/verify') {
          setIsVisible(true);
        } else {
          setIsVisible(false);
        }
      } catch (error) {
        console.error('Error checking verification status:', error);
      }
    };
    
    checkVerificationStatus();
  }, [location.pathname, axiosWithAuth]);
  
  // الانتقال إلى صفحة التحقق
  const handleVerifyNow = () => {
    navigate('/verify');
  };
  
  // إخفاء التحذير مؤقتًا
  const handleSkip = () => {
    setIsVisible(false);
  };
  
  return (
    <AnimatePresence>
      {isVisible && (
        <WarningContainer
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 50 }}
          transition={{ duration: 0.3 }}
        >
          <WarningHeader>
            <WarningIcon>
              <FiAlertTriangle size={18} />
            </WarningIcon>
            <WarningTitle>تنبيه</WarningTitle>
            <CloseButton onClick={handleSkip}>
              <FiX size={18} />
            </CloseButton>
          </WarningHeader>
          
          <WarningMessage>
            ❗ بريدك الإلكتروني غير مُفعل بعد. من فضلك قم بتفعيله الآن.
          </WarningMessage>
          
          <ButtonsContainer>
            <ActionButton 
              className="primary"
              primary
              onClick={handleVerifyNow}
            >
              تحقق الآن
            </ActionButton>
            
            <ActionButton 
              className="secondary"
              onClick={handleSkip}
            >
              تخطي مؤقتًا
            </ActionButton>
          </ButtonsContainer>
        </WarningContainer>
      )}
    </AnimatePresence>
  );
};

export default EmailVerificationWarning;